import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const EMPTY = { address: "", host: "", port: 993, username: "", password: "", owner_id: "", save_body: true, enabled: true };

function MailboxDialog({ open, onOpenChange, initial, users, defaultHost, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial, password: "" } : { ...EMPTY, host: defaultHost }); }, [open, initial, defaultHost]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, port: Number(f.port) || 993, password: f.password || null, username: f.username || null };
      if (initial) await api.put(`/admin/mailboxes/${initial.id}`, body); else await api.post("/admin/mailboxes", body);
      toast.success("Boîte connectée"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mailbox-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? "Modifier la boîte mail" : "Ajouter une boîte mail"}</DialogTitle>
          <DialogDescription>La connexion est testée avant l'enregistrement. Les mails restent intacts dans la boîte (lecture seule).</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div><Label>Adresse</Label><Input value={f.address} onChange={set("address")} placeholder="compta@sdrpneus.fr" data-testid="mailbox-address-input" /></div>
          <div className="grid grid-cols-[1fr_90px] gap-2">
            <div><Label>Serveur IMAP</Label><Input value={f.host} onChange={set("host")} data-testid="mailbox-host-input" /></div>
            <div><Label>Port</Label><Input value={f.port} onChange={set("port")} data-testid="mailbox-port-input" /></div>
          </div>
          <div><Label>Identifiant (si différent de l'adresse)</Label><Input value={f.username} onChange={set("username")} data-testid="mailbox-username-input" /></div>
          <div><Label>Mot de passe{initial ? " (laisser vide pour garder l'actuel)" : ""}</Label><Input type="password" value={f.password} onChange={set("password")} autoComplete="new-password" data-testid="mailbox-password-input" /></div>
          <div><Label>Les documents arrivent dans l'espace de</Label>
            <Select value={f.owner_id} onValueChange={set("owner_id")}>
              <SelectTrigger data-testid="mailbox-owner-select"><SelectValue placeholder="Choisir un collaborateur" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={f.save_body} onCheckedChange={(v) => setF({ ...f, save_body: !!v })} data-testid="mailbox-save-body-checkbox" />Enregistrer aussi le texte du mail en PDF</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={busy || !f.address || !f.owner_id || (!initial && !f.password)} data-testid="mailbox-save-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Tester et enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminMailboxes() {
  const [data, setData] = useState({ mailboxes: [], default_host: "" });
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/admin/mailboxes").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); api.get("/directory").then((r) => setUsers(r.data)); }, [load]);
  const name = (id) => users.find((u) => u.user_id === id)?.name || "—";
  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="admin-mailboxes">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold flex items-center gap-2"><Inbox className="h-4 w-4 text-[#1E3A8A]" />Boîtes mail de l'entreprise</div>
          <p className="text-sm text-slate-600 mt-1">Chaque matin à 6h, l'IA relève ces boîtes : les mails avec pièce jointe sont enregistrés (pièces jointes + mail en PDF), puis rangés dans la bonne société et le bon dossier. Les newsletters et mails automatiques sont ignorés.</p>
        </div>
        <Button size="sm" onClick={() => { setEdit(null); setOpen(true); }} data-testid="mailbox-add-btn"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>
      </div>
      <div className="divide-y border rounded-lg">
        {data.mailboxes.length === 0 && <p className="text-sm text-slate-500 p-4">Aucune boîte connectée.</p>}
        {data.mailboxes.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`mailbox-row-${b.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{b.address}</div>
              <div className="text-xs text-slate-500">Vers l'espace de {name(b.owner_id)} · {b.last_poll_at ? `relevée le ${fmtDate(b.last_poll_at, true)}` : "jamais relevée"}
                {b.last_status === "ok" && ` · ${b.last_imported || 0} document(s)`}
                {b.last_status === "erreur" && <span className="text-red-600" data-testid={`mailbox-error-${b.id}`}> · erreur : {b.last_error}</span>}</div>
            </div>
            <Switch checked={b.enabled} onCheckedChange={(v) => act(() => api.put(`/admin/mailboxes/${b.id}`, { ...b, enabled: v, password: null }))} data-testid={`mailbox-enabled-${b.id}`} />
            <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/mailboxes/${b.id}/poll`), "Relève lancée, actualisez dans une minute")} data-testid={`mailbox-poll-${b.id}`}><RefreshCw className="h-4 w-4 mr-1" />Relever</Button>
            <Button size="icon" variant="ghost" onClick={() => { setEdit(b); setOpen(true); }} aria-label="Modifier" data-testid={`mailbox-edit-${b.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => window.confirm(`Déconnecter ${b.address} ?`) && act(() => api.delete(`/admin/mailboxes/${b.id}`), "Boîte déconnectée")} aria-label="Supprimer" data-testid={`mailbox-delete-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <MailboxDialog open={open} onOpenChange={setOpen} initial={edit} users={users} defaultHost={data.default_host} onSaved={load} />
    </div>
  );
}
